/* ============================================================
   FGO HEADER — global behaviour
   ------------------------------------------------------------
   Split out of pdp-v2.js so the docking header works on every
   template. It used to bail immediately unless <body> carried
   the `pdp-v2` class, which only the product template sets.

   Owns three things: the measured promo-bar offset, the dock
   hysteresis, and the logo colour swap. Paired with
   fgo-header.css.
   ============================================================ */
/* ============================================================
   HEADER dock — hysteresis + measured promo height
   ------------------------------------------------------------
   The theme toggles .bg at a single threshold, so the header
   flickers when a scroll settles exactly on the boundary. The
   demo docks at one value and releases at a lower one, which
   cannot oscillate.

   Also measures the promo bar so the docked header sits under it
   rather than at a hardcoded offset.
   ============================================================ */
(function () {
  'use strict';

  var head = document.querySelector('#header');
  var promo = document.querySelector('[id*="theme-announcement"]');
  if (!head) return;

  /* --promo-h drives where the header sticks: top = --promo-h + 12 at rest,
     top = --promo-h when docked. Getting it wrong by writing 0 parks the
     header at the very top of the viewport, UNDER a promo bar that is
     z-index 101 — which is the bar sitting on top of the mark.

     This used to measure once, synchronously, at script execution, and then
     only again on resize. Run before the announcement bar has laid out,
     getBoundingClientRect().height returns 0, and 0 is what got written. The
     `var(--promo-h, 40px)` fallback in the CSS cannot save that: the property
     IS set, just set to zero, so the fallback never applies. The header then
     stayed under the bar for the life of the page unless you happened to
     resize the window, which is why it looked fine sometimes.

     Two guards. Never write a 0 while the element exists — remove the
     property instead, so the CSS fallback takes over. And keep measuring:
     on load, on font swap, and via ResizeObserver, since the bar is a
     rotating-message widget whose height can change after first paint. */
  var setPromoH = function () {
    if (!promo) {
      document.documentElement.style.setProperty('--promo-h', '0px');
      return;
    }
    var h = Math.round(promo.getBoundingClientRect().height);
    if (h > 0) document.documentElement.style.setProperty('--promo-h', h + 'px');
    else       document.documentElement.style.removeProperty('--promo-h');
  };
  setPromoH();
  addEventListener('resize', setPromoH, { passive: true });
  addEventListener('load', setPromoH);
  if (document.readyState === 'loading') {
    addEventListener('DOMContentLoaded', setPromoH);
  }
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(setPromoH).catch(function () {});
  }
  if (promo && window.ResizeObserver) {
    new ResizeObserver(setPromoH).observe(promo);
  }

  /* The theme's own handler (theme-header.js) decides docking from
     header.getBoundingClientRect().top <= 13. Our promo-bar offset
     (top: var(--promo-h)) makes that permanently false, so it never added
     .bg — and it strips the class on every scroll event, which defeated the
     cached `docked` flag this used to keep.

     It also drives the logo colour swap through .active on #headerLogo /
     #headerLogoAlt, so we cannot simply ignore it. Instead we state the full
     result unconditionally on every event, and register last (inside `load`,
     with a fresh function identity so it is a distinct listener) — listeners
     fire in registration order, so ours settles the class after the theme's. */

  var dark  = document.querySelector('#headerLogo');
  var light = document.querySelector('#headerLogoAlt');

  /* Two fixes for the flashing, and they are different problems.

     1. THE FLASH. The theme's handler strips .bg on every scroll event and we
        re-add it. Because the header transitions `background` over .25s, each
        strip/re-add restarted a cross-fade — which is the black-to-cream
        oscillation. Reading state off .bg made us complicit: the class we were
        reading was being cleared underneath us every frame.

        So our CSS now keys on `fgo-docked`, a class the theme has never heard
        of and therefore cannot remove. We still set .bg so the theme's own
        rules and anything else depending on it keep working, but nothing we
        style is driven by it. Our visual state changes only when the scroll
        state actually changes.

     2. THE BAND. 18/6 gave twelve pixels of hysteresis. One wheel notch is
        50-120px and momentum scrolling oscillates around a boundary, so the
        band was easy to straddle. 90/40 cannot be crossed by accident. */
  var DOCK_AT = 90, RELEASE_AT = 40;
  var docked = null;                 /* null = not yet applied */
  var ticking = false;

  var apply = function () {
    var y = window.scrollY;
    var next = docked ? y >= RELEASE_AT : y > DOCK_AT;

    /* .bg is kept in sync for the theme's benefit, cheaply and every time */
    if (head.classList.contains('bg') !== next) head.classList.toggle('bg', next);

    /* .active must ALSO be restated every event, not just on a state change.
       It is shared with the theme's handler, which rewrites it on every scroll
       event. Restating it only on transitions meant the theme won every event
       after the first — leaving the dark mark active on the black docked bar,
       i.e. an invisible logo. Same shared-ownership trap as .bg; toggle with
       an explicit force value is idempotent, so doing it every time is free. */
    if (dark  && dark.classList.contains('active')  === next)  dark.classList.toggle('active', !next);
    if (light && light.classList.contains('active') !== next) light.classList.toggle('active', next);

    if (next === docked) return;     /* geometry already correct */
    docked = next;
    head.classList.toggle('fgo-docked', next);
    /* Same state on <html>, because the element that actually carries the
       sticky offset is the Shopify section wrapper ABOVE #header, and a
       parent is not selectable from a child in CSS. See fgo-header.css. */
    document.documentElement.classList.toggle('fgo-head-docked', next);
  };

  /* rAF-throttled: scroll fires far faster than we can paint, and doing class
     work per event is what let the theme and this handler interleave mid-frame.

     A timer races the rAF because rAF starves in occluded windows, low-power
     mode and embedded panes — and a starved rAF here wedges `ticking` true
     forever, which kills docking for the life of the page. Whichever fires
     first wins; the loser no-ops. */
  var onScroll = function () {
    if (ticking) return;
    ticking = true;
    var done = false;
    var run = function () { if (done) return; done = true; ticking = false; apply(); direction(); };
    requestAnimationFrame(run);
    setTimeout(run, 120);
  };

  /* ---- DIRECTIONAL STICKY ------------------------------------------
     Hide on the way down, reveal on the way up — the reference's
     behaviour. Independent of docking: docking is a function of WHERE
     you are, this is a function of WHICH WAY you are going, and they
     compose (a revealed header at y=800 is still the docked variant).

     Three guards, each earning its place:
       DEADZONE  trackpads emit sub-pixel jitter in both directions;
                 without it the bar strobes when a finger rests still.
       REVEAL_AT above this the page is barely scrolled and hiding the
                 nav reads as a glitch rather than an intent.
       menu open never hide a bar the user is actively pointing at —
                 the mega panel is anchored to it and would fly away.  */
  /* 60, not 220. At 220 the bar survived two or three wheel notches, so it
     read as sticky-that-eventually-gives-up rather than as yielding to the
     reader. One notch is 50-120px, so 60 means the FIRST deliberate scroll
     down clears it — the reference's behaviour. Still above the noise floor,
     so a stray 10px nudge at the top of the page cannot trigger it. */
  var HIDE_BELOW = 60, DEADZONE = 6;
  var lastY = window.scrollY, hidden = false;

  var setHidden = function (want) {
    if (want === hidden) return;
    hidden = want;
    document.documentElement.classList.toggle('fgo-head-hidden', want);
  };

  var direction = function () {
    var y = window.scrollY;
    var dy = y - lastY;
    if (Math.abs(dy) < DEADZONE) return;
    lastY = y;

    /* the panel is positioned off the header; hiding it mid-hover
       would drag the menu the user is reading off the screen */
    var menuOpen = head.querySelector('#headerDrop') &&
                   head.matches(':hover');
    if (menuOpen) { setHidden(false); return; }

    if (y <= HIDE_BELOW) { setHidden(false); return; }
    setHidden(dy > 0);
  };

  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('load', function () {
    addEventListener('scroll', function () { onScroll(); }, { passive: true });
    apply();
  });
  apply();

  /* The top transition is gated behind this class (see fgo-header.css):
     first paint and the first measured correction must SNAP, not slide —
     a page should not open with its header animating into place. Double
     rAF = one fully painted frame with settled values; the timer covers
     contexts where rAF starves. Whichever fires first wins. */
  var readyOnce = false;
  var armReady = function () {
    if (readyOnce) return;
    readyOnce = true;
    document.documentElement.classList.add('fgo-head-ready');
  };
  requestAnimationFrame(function () { requestAnimationFrame(armReady); });
  setTimeout(armReady, 300);
})();

/* ==================================================================
   CINEMA HERO — the header dissolves over a full-bleed film.

   Only two things are needed beyond the CSS: a flag saying this page
   opens on a cinematic hero, and the header's real height, because the
   hero is pulled up by exactly that much so the FILM grows behind the
   bar while the copy inside stays put.

   Height is measured, never assumed: the header's resting and docked
   states differ, the promo bar may or may not be present, and a
   hardcoded value would leave a gap or a crop on some template.
   ================================================================== */
(function () {
  var hero = document.querySelector('[data-fgo-cinema-hero]');
  if (!hero) return;

  var head = document.getElementById('header');
  if (!head) return;

  var root = document.documentElement;

  var measure = function () {
    /* the RESTING height is what the hero must offset. Reading it while
       docked would pull the hero up by the wrong amount for the rest of
       the page's life, so skip the write whenever the bar is docked and
       keep the last good value. */
    if (head.classList.contains('fgo-docked')) return;
    /* Measure the WRAPPER, not the bar. #header carries 12px top and 24px
       bottom margin, so its own height (117) is ~36px short of the space it
       actually occupies (153) — and the hero was pulled up by the smaller
       number, leaving the film starting 73px below the top instead of
       running behind the bar. The Shopify section wrapper's box includes
       those margins, so it is the honest measurement. */
    /* The wrapper's box does NOT include the bar's margins — with no padding
       or border on the wrapper they collapse straight out of it, so reading
       the wrapper returned the same 117 as the bar itself. Add the margins
       explicitly; that is the space the hero has to climb over. */
    var cs = getComputedStyle(head);
    var h = Math.round(
      head.getBoundingClientRect().height +
      (parseFloat(cs.marginTop) || 0) +
      (parseFloat(cs.marginBottom) || 0)
    );
    if (h > 0) root.style.setProperty('--fgo-head-h', h + 'px');
  };

  measure();
  root.classList.add('fgo-cinema-hero');

  /* the mark scales and the pill has responsive padding, so the resting
     height is not constant across breakpoints */
  if ('ResizeObserver' in window) {
    new ResizeObserver(measure).observe(head);
  } else {
    window.addEventListener('resize', measure, { passive: true });
  }
  /* fonts land after first paint and change the bar's height with them */
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
})();

/* ==================================================================
   HEADER OFFSET — one measured value, every template.

   The sticky offset was a fixed 49px. That happens to equal today's
   promo bar (37px) plus the header's 12px gap, so it looks right —
   until the promo copy wraps to two lines, the bar is switched off, or
   a template renders without it. Each hero treatment then sits at a
   slightly different top spacing, which is the margin drift that shows
   up "depending on the header treatment".

   Measuring the promo bar and publishing it as a variable makes the
   offset correct by construction on all three heroes and at any
   viewport, instead of correct by coincidence at one.
   ================================================================== */
(function () {
  var root = document.documentElement;
  var promo = document.querySelector('[id*="announcement"]');
  var wrap  = document.querySelector('[id*="theme-header"]');
  if (!wrap) return;

  var apply = function () {
    var h = promo ? Math.round(promo.getBoundingClientRect().height) : 0;
    root.style.setProperty('--fgo-promo-h', h + 'px');
  };

  apply();
  if ('ResizeObserver' in window && promo) {
    new ResizeObserver(apply).observe(promo);
  } else {
    window.addEventListener('resize', apply, { passive: true });
  }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(apply);
}());

  /* ---- HERO OVERLAY PULL (homepage) --------------------------------
     The hero must begin at the promo bar's lower edge so the header
     card floats ON the photograph, as the reference does. The header's
     section wrapper sits between them in the DOM and occupies flow
     height, so the hero is pulled up by exactly the distance from the
     promo bar's bottom to the wrapper's bottom.

     MEASURED, not assumed: that distance is the wrapper's own height
     plus whatever gap precedes it, and both move with the promo bar's
     height, the header's margins and the viewport. A hardcoded value
     was 40px short at this breakpoint alone.

     Read at scrollY 0 only — once anything is stuck the rects no
     longer describe the resting layout. */
  (function () {
    var root = document.documentElement;
    var measurePull = function () {
      if (window.scrollY > 0) return;
      var wrap  = document.querySelector('[id*="theme-header"]');
      var promo = document.querySelector('[id*="theme-announcement"]');
      if (!wrap) return;
      var wb = wrap.getBoundingClientRect().bottom;
      var pb = promo ? promo.getBoundingClientRect().bottom : 0;
      var pull = Math.round(wb - pb);
      if (pull > 0) root.style.setProperty('--fgo-head-pull', pull + 'px');
    };
    measurePull();
    addEventListener('load', measurePull);
    addEventListener('resize', measurePull);
  })();
